import {
  type TiptapBulletList,
  type TiptapDocContent,
  type TiptapHeadingContent,
  type TiptapListItem,
  type TiptapMark,
  type TiptapOrderedList,
  type TiptapParagraphContent,
  type TiptapTextContent,
} from '../model/tiptap'
import { isNotEmpty } from './isEmpty'

type TiptapBlockContent = TiptapHeadingContent | TiptapParagraphContent | TiptapBulletList | TiptapOrderedList
type TiptapListItemContent = TiptapBulletList | TiptapOrderedList | TiptapParagraphContent

export function tiptapToMarkdown(content: TiptapDocContent): string {
  return content.content.map(convertBlock).join('\n\n').trim()
}

function applyMarks(text: string, marks?: TiptapMark[]): string {
  if (!marks?.length) return text

  let result = text
  for (const mark of marks) {
    if (mark.type === 'bold') {
      result = `**${result}**`
    } else if (mark.type === 'underline') {
      result = `<u>${result}</u>`
    } else if (mark.type === 'strike') {
      result = `~~${result}~~`
    } else if (mark.type === 'link') {
      result = `[${result}](${mark.attrs.href})`
    }
  }
  return result
}

function convertTextNode(node: TiptapTextContent): string {
  return applyMarks(node.text, node.marks)
}

function convertParagraph(content: TiptapParagraphContent): string {
  if (!content.content?.length) return ''
  return content.content.map(convertTextNode).join('')
}

function convertHeading(content: TiptapHeadingContent): string {
  const prefix = '#'.repeat(content.attrs.level) + ' '
  return prefix + content.content.map(convertTextNode).join('')
}

function convertListItemContent(content: TiptapListItemContent, indent: string): string {
  if (content.type === 'paragraph') {
    return convertParagraph(content)
  }
  if (content.type === 'bulletList') {
    return convertBulletList(content, indent)
  }
  return convertOrderedList(content, indent)
}

function convertListItem(item: TiptapListItem, prefix: string, indent: string): string {
  const [first, ...rest] = item.content
  if (!first) return ''

  const nestedIndent = indent + ' '.repeat(prefix.length)
  const firstText = convertListItemContent(first, nestedIndent)
  const restTexts = rest.map((node) => convertListItemContent(node, nestedIndent)).filter(isNotEmpty)

  const lines = [indent + prefix + firstText, ...restTexts]
  return lines.join('\n')
}

function convertBulletList(content: TiptapBulletList, indent = ''): string {
  return content.content.map((item) => convertListItem(item, '- ', indent)).join('\n')
}

function convertOrderedList(content: TiptapOrderedList, indent = ''): string {
  const start = content.attrs.start
  return content.content.map((item, index) => convertListItem(item, `${start + index}. `, indent)).join('\n')
}

function convertBlock(block: TiptapBlockContent): string {
  if (block.type === 'paragraph') return convertParagraph(block)
  if (block.type === 'heading') return convertHeading(block)
  if (block.type === 'bulletList') return convertBulletList(block)
  return convertOrderedList(block)
}
